import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserDto } from '../models/user/user-dto';
import { UserMicroservice } from '../services/api/user-api';

export type RoleGuard = CanActivateFn & {
  /** The role check behind the guard, also used to decide nav visibility. */
  hasRole: (user: UserDto | null) => boolean;
};

/**
 * Creates a guard that allows the loaded application user when `hasRole`
 * passes, and redirects everyone else (including a not yet loaded user) to `/`.
 *
 * The user is read synchronously, so it must already be loaded: put role guards
 * in `canActivateChild` next to `authGuard` in `canActivate`.
 */
export function createRoleGuard(
  hasRole: (user: UserDto) => boolean
): RoleGuard {
  const check = (user: UserDto | null): boolean => !!user && hasRole(user);

  const guard: CanActivateFn = () =>
    check(inject(UserMicroservice).applicationUser$.getValue()) ||
    inject(Router).createUrlTree(['/']);

  return Object.assign(guard, { hasRole: check });
}

export function isRoleGuard(guard: unknown): guard is RoleGuard {
  return typeof guard === 'function' && 'hasRole' in guard;
}
